import type {OwayoJSKonfiViewer} from "./owayoJSViewerWithSelObs";
import {HandleShaderCreator, ShaderCreator} from "./shader/OwayoShader";
import type {PartSelectableObject} from "./partSelOb";
import type {Z_OrderTable} from "./zOrderTable";
import type {InteractionHandle} from "./interactionHandleBaseClass";
import {UtilityPlane} from "./utilityPlane";
import {THREE} from "./resource/BVH.js";
import {CONSTANTS} from "./meta_data";
import {Vector2, Vector2WithUV} from "./delme_globals";
import {TaskCounter} from "./taskCounter";
import {negate} from "../global/functionalTools";
import {VIEWERFEATURE} from "./debugMenu";
import {arrayElementByKeyValue, arrayGetElementByNoneCaseSensitiveName} from "../global/arrayHelper";
import {warnIfDebug} from "../global/logFunctions";

export class BoxRelativeToPart {
    public bottomLeft:Vector2;
    public topRight:Vector2;

    public constructor(bottomLeft:Vector2, topRight:Vector2) {
        this.bottomLeft = bottomLeft;
        this.topRight = topRight;
    }
}

export enum GreyOutState {
    NOT_GREYED_OUT,
    GREYED_OUT,
    OUT_OF_DRUCKBEREICH_GREYED_OUT
}




export class OwayoKonfiPart {
    selectableObjects:PartSelectableObject[]=[];
    hitmapContext2D:CanvasRenderingContext2D|null = null;
    handles : InteractionHandle[] = [];
    hitmapImageData:ImageData|null=null;
    parent:OwayoJSKonfiViewer;
    isGreyedOut:GreyOutState=GreyOutState.NOT_GREYED_OUT;
    owayoMaterial={stoff: null as string|null, colorCode: null as string|null};
    zOrderTable:Z_OrderTable;
    offsetRepeat:THREE.Vector4;
    sperrBezirke = [];
    name : string;
    mesh : THREE.Mesh;
    hasZentralesAbgelegtesNichtEinfaerbbaresPartImage  = true;
    visible  = true;
    map=null;
    alphaMap=null;
    specularMap=null;
    bumpMap=null;
    normalMap=null;
    shadowMap=null;
    overlayMap=null;
    overlayIntensity = 1.0;
    overlayScale={x:1,y:1};
    overlayHelligkeitsSummand=-0.5;
    overlayAnzeigenAufFarbenIntArray:number[]=[];
    bumpScale =0.0;
    normalScale =0.0;
    shininess =0.0;
    specularMin =0.0;
    specular =0.0;
    colorTexture : THREE.Texture;
    textureSize : Vector2;
    partSizeCM : Vector2;
    partStauchungsFaktor =1;
    pendingTasks: TaskCounter;
    shaders:THREE.ShaderMaterial[]|THREE.MeshLambertMaterial[] = [];
    handleAndGuideLayer: UtilityPlane;
    hitmapCanvas: HTMLCanvasElement;
    handleShader: any
    shadowCanvas :HTMLCanvasElement|null = null ;
    usedScaleBecauseOfMaxTextureSize: number;
    druckBereich:BoxRelativeToPart|null = null;
    nichtDruckBereich:BoxRelativeToPart|null = null;
    motivBereich:BoxRelativeToPart|null = null;
    isDroppableLegacyFromStoff = false;
    innenseiteAufhellen = true;
    schwarzAufhellenImDruckbereich = false;

    public hasDefinedDruckbereich = ()=>this.druckBereich !== null;
    patternAnker: Vector2|null;

    constructor(parent:OwayoJSKonfiViewer, mesh, zOrderTable) {
        this.parent = parent;
        this.name = mesh.name;
        this.mesh = mesh;
        this.zOrderTable = zOrderTable;
        this.initColorTextur();
        this.textureSize = new Vector2(0, 0);
        this.partSizeCM	= new Vector2(0, 0);
        this.initHitMapCanvas();
        this.patternAnker = new Vector2(0,0);
        this.pendingTasks = new TaskCounter(
            this, null,
            ()=>this.pendingTasksComplete(),
            "OwayoKonfiPart Constructor"
        );
        this.handleAndGuideLayer = new UtilityPlane(this);
        this.handleShader = HandleShaderCreator.createHandleShader(this);
        this.offsetRepeat = new THREE.Vector4(0.0, 0.0, 1.0, 1.0);
    }

    private initColorTextur() {
        this.colorTexture  	= new THREE.Texture();
        this.colorTexture.minFilter = THREE.NearestFilter;
        this.colorTexture.magFilter = THREE.NearestFilter;
        this.colorTexture.generateMipmaps = false;
    }

    private initHitMapCanvas() {
        this.hitmapCanvas = document.createElement('canvas');
        this.hitmapCanvas.width = CONSTANTS.HITMAP_SIZE.width;
        this.hitmapCanvas.height = CONSTANTS.HITMAP_SIZE.height;
        this.hitmapContext2D = this.hitmapCanvas.getContext('2d', {willReadFrequently:true});
    }

    private pendingTasksComplete() {
        if (this.parent.selectedObject !== null && this.parent.selectedObject.getParentPartName() === this.name) {
            this.parent.selectedObject.placeAppropriateHandles();
        }
        const entangled = this.parent.entangledObjects;
        for (let i = 0, len = entangled.length; i < len; i++) {
            if (entangled[i].getParentPartName() === this.name) {
                entangled[i].placeAppropriateHandles();
            }
        }
        this.parent.utilityPainter.repaint();
        ShaderCreator.createShaders(this, this.parent.getPatterns(this.name));
        this.parent.render();
    }

    private static getHitmapPixelNumber(x,y, textureWidth) {
        // ImageData ist kein 2 Dimensionales Array sondern ein einfaches Array.
        // Deshalb muss man pro y Koordinate einmal die komplette Pixelbreite des Canvases rechnen.
        //
        // Achtung! wir haben pro pixel R,G,B,A Werte wir müssen also pro Pixel vier postionen im Array
        // weiter gehen.  Danach ist i+0 = r, i+1 = g, i+2 = b, i+3 = a
        return (y * textureWidth * CONSTANTS.HITMAP_BYTES_PER_PIXEL)+ x * CONSTANTS.HITMAP_BYTES_PER_PIXEL;
    }

    private getHitColorCodeInHitmapFromUV(u:number,v:number):number {
        const hitmapX = Math.round(u * this.hitmapCanvas.width);
        // UV - Koordinaten denken von links unten.
        const hitmapY = this.hitmapCanvas.height - Math.round(v * this.hitmapCanvas.height);
        const hitmapPixelNumber = OwayoKonfiPart.getHitmapPixelNumber(hitmapX, hitmapY, this.hitmapCanvas.width);
        return (this.hitmapImageData as ImageData).data[hitmapPixelNumber];
    }

    getHitColorCode(hitPosition:{u:number,v:number}):string|null {
        if(this.hitmapImageData === null) {
            return null;
        }
        const hitcolorCodeNumber = this.getHitColorCodeInHitmapFromUV(hitPosition.u, hitPosition.v);
        return this.parent.colorCodes.reduce(function(carry:string|null, color) {
            if (color.colorCodeNr === hitcolorCodeNumber) {
                if(VIEWERFEATURE.LOG_HIT_PARTS_in_CONSOLE) {
                    console.log("hit colorcode: "+color.colorCode);
                }
                return color.colorCode;
            }
            return carry;
        }, null);
    }

    private getHitSelectableObjectID(hitPosition:Vector2):string|null {
        if(this.parent.selectedObject === null) {
            return null;
        }
        if (this.parent.selectedObject.covers(this.name, hitPosition)) {
            return this.parent.selectedObject.id;
        }
        for (let i = 0, len = this.parent.entangledObjects.length; i < len; i++) {
            if (this.parent.entangledObjects[i].covers(this.name, hitPosition)) {
                return this.parent.entangledObjects[i].id;
            }
        }
        return null;
    }

    /**
     * Gibt zurück, welches SelectableObject, welcher Handle und welche Farbe getroffen wurde bei einem Mausklick
     * übergeben wird der u und v Wert, auf den in einem Part geklickt wurde
     */
    getHitObjectAndColor(hitPosition:Vector2WithUV):{selectableObjectID, colorCode, hitHandler} {
        const result = {
            colorCode: this.getHitColorCode(hitPosition),
            selectableObjectID: null as null|string,
            hitHandler: null as null|string
        };

        const hitHandler = this.handleIdUnderPosition(hitPosition);
        if (hitHandler !== null) {
            result.hitHandler = hitHandler;
            return result;
        }

        if (this.parent.selectedObject !== null) {
            const hitSelectableObjectID = this.getHitSelectableObjectID(hitPosition);
            if(hitSelectableObjectID !== null) {
                result.selectableObjectID = hitSelectableObjectID;
                return result;
            }
        }

        const selectableObjectID = this.selectableObjectUnderPosition(hitPosition);
        if (selectableObjectID !== null) {
            result.selectableObjectID = selectableObjectID;
            return result;
        }

        const hitPatternSelObID = this.getHitPatternSelOb(result.colorCode);
        if (hitPatternSelObID !== null) {
            result.selectableObjectID = hitPatternSelObID;
        }

        return result;
    }

    private selectableObjectUnderPosition(hitPosition:Vector2):string|null {
        const $this = this;
        return this.selectableObjects.reduce(function(carry, partSelOb){

            if (partSelOb.covers($this.name, hitPosition)) {
                const zIndex = $this.zOrderTable.getZIndex(partSelOb.id);
                const hitNonPinnedSelObAndCarryIsPinned = p=>carry.isPinned === true && p.isPinned === false;
                const hitPinnedSelObThatIsOverPinnedCarry = (p, zindex)=>carry.isPinned === true && p.isPinned === true && zindex > carry.zOrder;
                const hitPartSelObOverCarryBothNotPinned = (p, zindex)=>carry.isPinned === false && p.isPinned === false && zindex > carry.zOrder;

                if(hitNonPinnedSelObAndCarryIsPinned(partSelOb)) {
                    carry.isPinned = false;
                    carry.zOrder = zIndex;
                    carry.id = partSelOb.id;
                }

                if(hitPinnedSelObThatIsOverPinnedCarry(partSelOb, zIndex)) {
                    carry.zOrder = zIndex;
                    carry.id = partSelOb.id;
                }

                if(hitPartSelObOverCarryBothNotPinned(partSelOb, zIndex)) {
                    carry.zOrder = zIndex;
                    carry.id = partSelOb.id;
                }
            }
            return carry;
        }, {id:null, zOrder:-Infinity, isPinned:true}).id;
    }

    private handleIdUnderPosition(hitPosition):string|null {
        return this.handles.reduce(function(carry:string|null, handle){
            if(carry !== null) {
                return carry;
            }
            if (handle.covers(hitPosition)) {
                return handle.id;
            }
            return null;
        }, null);
    }

    setGreyOutState(shouldBeGreyedOut:GreyOutState) {
        if (this.isGreyedOut === shouldBeGreyedOut) {
            return;
        }
        this.isGreyedOut = shouldBeGreyedOut;
        this.shaders[0].uniforms.isGreyedOut.value = (shouldBeGreyedOut === GreyOutState.GREYED_OUT ? 1 : 0);
        this.shaders[0].uniforms.isGreyedOutExceptDruckbereich.value = (shouldBeGreyedOut === GreyOutState.OUT_OF_DRUCKBEREICH_GREYED_OUT ? 1 : 0)
    }

    orderSelObsByZIndex() {
        const oldArray = this.selectableObjects.slice(0);
        this.selectableObjects.sort(function (a, b) {
            return a.zIndex() - b.zIndex();
        });

        for (let i = 0, len = this.selectableObjects.length; i < len; i++) {
            if (oldArray[i] !== this.selectableObjects[i]) {
                return true; //change Happened
            }
        }
        return false; //no change Happened
    }

    insertPartSelOb(partSelOb:PartSelectableObject) {
        this.selectableObjects.push(partSelOb);
        this.selectableObjects = this.selectableObjects.sort(function (a, b) {
            return a.zIndex() - b.zIndex();
        });
        //d.h. a.zIndex < b.zIndex ==> a hat kleineren Index in Endarray
    }

    getCounterpart():OwayoKonfiPart|null {
        if (this.name.indexOf("_optional")>-1){
            return null;
        }
        const searchValuesWithReplacements = [
            {s:/Left/i, r:'Right'},
            {s:/Right/i, r:'Left'},
            {s:/Links/i, r:'Rechts'},
            {s:/Rechts/i, r:'Links'},
            {s:/Rechter/i, r:'Linker'},
            {s:/Linker/i, r:'Rechter'},
        ];
        const $this = this;
        return searchValuesWithReplacements.reduce(function (carry: OwayoKonfiPart | null, searchreplace: { s, r }) {
            if (carry !== null) {
                return carry;
            }
            const newPartname = $this.name.replace(searchreplace.s, searchreplace.r);
            if (newPartname !== $this.name) {
                carry = arrayGetElementByNoneCaseSensitiveName($this.parent.modelParts, newPartname);
            }
            return carry;
        }, null);

    }

    private getHitPatternSelOb(hitColorCode) {
        const firstPatternSelOb = arrayElementByKeyValue(this.selectableObjects, "repeat", true);
        if(firstPatternSelOb !== null) {
            if(firstPatternSelOb.clampToColor === -1 || hitColorCode === String.fromCharCode(firstPatternSelOb.clampToColor + CONSTANTS.KEYCODE.A - 1) ) {
                return firstPatternSelOb.id;
            }
        }
        return null;
    }

    public setVisibility(visible:boolean) {
        (this.mesh as THREE.Mesh).visible = visible;
        this.visible = visible;
    }

    disposeShaders() {
        for (let i = 0; i < this.shaders.length; i++) {
            this.shaders[i].dispose();
        }
    }

    disposeMesh() {
        if (this.mesh !== null) {
            if (this.mesh.geometry) {
                this.mesh.geometry.dispose();
            }
            if (this.mesh.material) {
                this.mesh.material.dispose();
            }

            this.mesh = null;
        }
    }

    static isNullOrUndefined = o=>typeof(o)==="undefined" || o === null;

    disposeHandleAndGuideLayer() {
        this.handleAndGuideLayer.textureCanvas = null;
        this.handleAndGuideLayer.textureContext2D = null;
        this.handleAndGuideLayer.texture.dispose();
        this.handleAndGuideLayer.texture = null;
    }

    dispose() {
        const notNullOrUndefined = negate(OwayoKonfiPart.isNullOrUndefined);
        const disposeAndNullify = (propertyName)=> {
            if(notNullOrUndefined(this[propertyName])) {
                this[propertyName].dispose();
                this[propertyName] = null;
            }
        }

        this.disposeShaders();
        // this.colorCanvas = null;
        (this.colorTexture as THREE.Texture).dispose();
        this.hitmapCanvas = null;

        this.disposeMesh();
        disposeAndNullify("handleShader");
        disposeAndNullify("map");
        disposeAndNullify("shadowMap");

        if(notNullOrUndefined(this.handleAndGuideLayer)) {
            this.disposeHandleAndGuideLayer();
        }

        // Beni 14.10.2015:
        // Die Stoff Texturen sind einmal zentral gespeichert und geladen und dürfen daher hier nicht
        // disposed werden. statt dessen löschen wir nur die Referenz auf die Textur
        this.alphaMap = null;
        this.bumpMap = null;
        this.specularMap = null;
    }

    loadShadowTexture(pathToShadowImage){
        const $this = this;
        const imageObj = new Image();
        imageObj.onload = function(){
            $this.shadowMap  	= new THREE.Texture();
            ($this.shadowMap as THREE.Texture).minFilter = THREE.NearestFilter;
            ($this.shadowMap as THREE.Texture).magFilter = THREE.NearestFilter;
            ($this.shadowMap as THREE.Texture).generateMipmaps = false;

            // Wenn unsere Texturen kleiner sind als das ShadowImage, dann liegt das entweder an der
            // Grafikkarte oder an einer Geschwindigikeits einstellung.
            // Auf jeden Fall müssen wir dann das ShadowImage auf die größe der Texturen schrumpfen.
            if ($this.textureSize.x < imageObj.width || $this.textureSize.y < imageObj.height)
            {
                $this.shadowCanvas   = document.createElement('canvas');
                $this.setShadowCanvasValues(imageObj);
            }
            else
            {
                ($this.shadowMap as THREE.Texture).image = imageObj;
            }

            ($this.shadowMap as THREE.Texture).needsUpdate = true;
            ShaderCreator.createShaders($this, $this.parent.getPatterns($this.name));
            $this.parent.render();
            $this.parent.viewerTasks.taskFinished("loadColorTexture");

        };
        imageObj.onerror = function() {
            warnIfDebug("could not load part.ShadowTexture");
            $this.parent.viewerTasks.taskFinished("loadColorTexture");
        };
        imageObj.src =	pathToShadowImage; //löst das Laden aus
    }

    private setShadowCanvasValues(imageObj) {
        (this.shadowCanvas as HTMLCanvasElement).width = this.textureSize.x;
        (this.shadowCanvas as HTMLCanvasElement).height = this.textureSize.y;
        ((this.shadowCanvas as HTMLCanvasElement).getContext('2d') as CanvasRenderingContext2D).drawImage(imageObj,0,0,this.textureSize.x,this.textureSize.y) ;
        (this.shadowMap as THREE.Texture).image = this.shadowCanvas;
    }

    loadPartColor(pathToColorCode, designTextureTaskCounter:TaskCounter){
        const $this = this;

        const imageObj = new Image();
        imageObj.onload = function(){
            $this.textureSize.x = imageObj.width;
            $this.textureSize.y = imageObj.height;
            ($this.colorTexture as THREE.Texture).image = imageObj;
            ($this.colorTexture as THREE.Texture).needsUpdate = true;
            ShaderCreator.createShaders($this, $this.parent.getPatterns($this.name));
            $this.parent.render();

            // Zeichne die Textur ins HitMap-Canvas
            // Die verwenden wir um später zu wissen auf welche farbe jemand gedrückt hat.
            ($this.hitmapContext2D as CanvasRenderingContext2D).drawImage(imageObj, 0, 0, CONSTANTS.HITMAP_SIZE.width, CONSTANTS.HITMAP_SIZE.height);
            $this.hitmapImageData = ($this.hitmapContext2D as CanvasRenderingContext2D).getImageData(0, 0, CONSTANTS.HITMAP_SIZE.width, CONSTANTS.HITMAP_SIZE.height);
            $this.parent.viewerTasks.taskFinished("loadColorTexture");
            designTextureTaskCounter.taskFinished()
        };
        imageObj.onerror = function() {
            warnIfDebug("could not load partColorTexture");
            $this.parent.viewerTasks.taskFinished("loadColorTexture");
        };
        imageObj.src =	pathToColorCode; //löst das Laden aus
    }
}

